import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as authenticationService from '../../../data-layer/authentication/service';
import {
    addNewCollectionIdentifiers,
    setCollectionsRecords,
} from '../../../data-layer/collections/actions';
import * as collectionsService from '../../../data-layer/collections/service';
import { DEFAULT_COLLECTION_ID } from '../../../data-layer/collections/service';
import { showNotificationAsync } from '../../../data-layer/notifications/actions';
import { getPostSuggests } from '../../../data-layer/posts/service';
import { useDispatch } from '../../../data-layer/useDispatch';
import { useCpaTrial } from '../../../ui/users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { BookmarkStep } from '../../../ui/users/SaveToCollectionDrawer/types';
import { SaveToCollectionPage } from '../../../ui/users/SaveToCollectionPage/SaveToCollectionPage';
import { contentActionsService } from '../../../utils/ContentActionsService/ContentActionsService';
import { EventSource } from '../../../utils/analyticsService';
import { closeSlidePanel } from '../../../utils/closeSlidePanel';
import { useCollectionInfo } from '../../users/hooks/useCollectionInfo';
import { useCollectionsController } from '../../users/useCollectionsController';
import { usePostModel } from '../usePostModel';

/** Страница сохранения поста в коллекцию для поискового приложения */
export const PostSaveToCollection = () => {
    const params = useParams();
    const postId = params.postId!;
    const [isPostError, setIsPostError] = useState(false);
    const handlePostError = useCallback(() => setIsPostError(true), []);
    const post = usePostModel({ postId, onError: handlePostError });
    const dispatch = useDispatch();
    const controller = useCollectionsController({ skipSavedCollections: true });
    const { isNotRegistered, refreshUserProfile } = useCpaTrial();

    const screen = EventSource.SEARCH_APP_FULLSCREEN;

    const [step, setStep] = useState<BookmarkStep>(BookmarkStep.ADD_TO_EXIST_COLLECTION);
    const [isSaved, setIsSaved] = useState(false);
    const [savedCollectionName, setSavedCollectionName] = useState<string | undefined>();

    const hasShownLoggedRef = useRef(false);

    const onAddClick = useCallback(() => {
        contentActionsService.trackCollectionCreateDialogOpen({
            id: postId,
            screen,
        });
        setStep(BookmarkStep.ADD_TO_NEW_COLLECTION);
    }, [postId, screen]);

    const { values, handlers } = useCollectionInfo({
        initialValues: {
            numberOfPosts: 1,
            displayName: '',
            isPublic: true,
        },
        id: postId,
        getSuggestMethod: () => getPostSuggests(postId, screen),
        needLoadSuggests: step === BookmarkStep.ADD_TO_NEW_COLLECTION,
        screen,
        mode: 'create',
    });

    const onSaveToExistentCollection = authenticationService.protect(
        async (collectionId: string) => {
            const collectionModel = controller.createdCollections.find(
                (collection) => collection.id === collectionId,
            );

            try {
                const response = await collectionsService.saveToExistentCollection(
                    collectionId,
                    postId,
                );
                dispatch(setCollectionsRecords(...response.records.collections));

                contentActionsService.trackAddPostToCollection({
                    postId,
                    collectionId,
                    screen,
                });

                setSavedCollectionName(collectionModel?.displayName);
                setIsSaved(true);
            } catch (_error) {
                dispatch(showNotificationAsync('Не удалось сохранить пост в коллекцию'));
            }
        },
    );

    const onSaveToNewCollection = authenticationService.protect(async () => {
        try {
            const response = await collectionsService.saveToNewCollection({
                postIds: [postId],
                collectionName: values.showInFeed ? values.currentSuggest : values.collectionName,
                isPublic: values.isPublic,
                showInFeed: values.showInFeed,
                needCpaRegistration: isNotRegistered,
            });
            dispatch(setCollectionsRecords(...response.records.collections));
            dispatch(
                addNewCollectionIdentifiers({
                    namespace: 'default',
                    identifiers: response.collections,
                }),
            );
            contentActionsService.trackCollectionCreated({
                id: response.collections[0],
                postIds: [postId],
                screen,
                newValues: {
                    displayName: response.records.collections[0].displayName,
                    showInFeed: response.records.collections[0].feedStatus?.showInFeed,
                    isPublic: response.records.collections[0].isPublic,
                },
            });

            setSavedCollectionName(response.records.collections[0].displayName);
            setIsSaved(true);
        } catch (_error) {
            dispatch(showNotificationAsync('Не удалось сохранить пост в коллекцию'));
        }

        if (isNotRegistered) {
            refreshUserProfile();
        }
    });

    useEffect(() => {
        if (!hasShownLoggedRef.current && post) {
            hasShownLoggedRef.current = true;

            contentActionsService.trackSaveToCollectionDialogOpen({
                id: postId,
                screen,
            });
        }
    }, [post, postId, screen]);

    useEffect(() => {
        if (!isSaved) {
            return;
        }
        const timerId = setTimeout(closeSlidePanel, 1000);
        return () => clearTimeout(timerId);
    }, [isSaved]);

    const createdCollections = controller.createdCollections.filter(
        (collection) => collection.id !== DEFAULT_COLLECTION_ID,
    );

    const isCollectionsLoading =
        controller.isLoading || (!controller.isLoaded && createdCollections.length === 0);

    const isError = isPostError || controller.isError;

    const saveToCollectionPageProps = {
        isCollectionsLoading,
        collections: createdCollections,
        onSaveToExistentCollection,
        values,
        handlers: { ...handlers, onSubmit: onSaveToNewCollection },
        step,
        onAddClick,
        isNotRegistered,
        isSaved,
        isError,
        collectionName: savedCollectionName,
        onClose: closeSlidePanel,
    };

    return <SaveToCollectionPage {...saveToCollectionPageProps} />;
};
